import type { LaunchReadiness } from './types';
import { anonymousAuthAllowed, getAccessToken } from '@/auth/token';
import type {
  AirdropRequest,
  AirdropResponse,
  ApiErrorBody,
  ApiErrorDetails,
  BalanceResponse,
  CandleField,
  CandlesResponse,
  CandleTf,
  CoinDetail,
  CoinSort,
  CoinsResponse,
  CoinWindow,
  ConfigResponse,
  HealthResponse,
  HoldersResponse,
  IdentityLinkResponse,
  IdentityUnlinkResponse,
  LeaderboardResponse,
  LeaderboardWindow,
  MediaResponse,
  MetadataRequest,
  MetadataResponse,
  ProfileResponse,
  SearchResponse,
  TradesResponse,
  WalletIdentitiesResponse,
  WalletIdentity,
} from './types';
import type { BuyTxRequest, LaunchTxRequest, LaunchTxResponse, QuoteQuery, QuoteResponse, SellTxRequest, SendTxRequest, SendTxResponse, TradeTxResponse } from './tx-types';

/** API origin. Empty = same origin (the Vite dev server proxies /api, /m, /media and /ws to the server or the mock). */
export const API_BASE: string = import.meta.env.VITE_API_URL?.replace(/\/$/, '') ?? '';

export function wsUrl(): string {
  if (API_BASE) return API_BASE.replace(/^http/, 'ws') + '/ws';
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${location.host}/ws`;
}

/**
 * An error answer from the API (`{ error: { code, message, details? } }`), or a client-side failure: code `network`
 * (fetch threw: offline, DNS, CORS, a timeout) or `http_<status>` (an error without a JSON body, e.g. the edge during a
 * deploy). Branch on `code`, not on the status alone.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: ApiErrorDetails | undefined;
  /** Seconds to wait before retrying: details.retryAfterSec, else the Retry-After header. */
  readonly retryAfterSec: number | undefined;
  constructor(status: number, code: string, message: string, details?: ApiErrorDetails, retryAfterSec?: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.retryAfterSec = retryAfterSec;
  }
}

/** No Privy session for a route that needs one (or the server refused the token twice): call login() and keep the form. */
export class NeedsLoginError extends ApiError {
  constructor(message = 'Log in to continue.') {
    super(401, 'unauthorized', message);
    this.name = 'NeedsLoginError';
  }
}

/** Retry-After as seconds (delta-seconds or an HTTP date); undefined when absent or unreadable. */
export function parseRetryAfter(header: string | null | undefined): number | undefined {
  if (!header) return undefined;
  const s = Number(header);
  if (Number.isFinite(s) && s >= 0) return s;
  const at = Date.parse(header);
  return Number.isFinite(at) ? Math.max(0, Math.ceil((at - Date.now()) / 1000)) : undefined;
}

/** The caller's signal plus a timeout. `timedOut()` tells our own timeout from the caller's abort. */
function withTimeout(signal: AbortSignal | null | undefined, ms: number) {
  const ctl = new AbortController();
  let fired = false;
  const timer = setTimeout(() => {
    fired = true;
    ctl.abort();
  }, ms);
  const onAbort = () => ctl.abort();
  if (signal) {
    if (signal.aborted) ctl.abort();
    else signal.addEventListener('abort', onAbort, { once: true });
  }
  return {
    signal: ctl.signal,
    timedOut: () => fired,
    done: () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
    },
  };
}

export interface ReqInit extends RequestInit {
  /** Send the Privy access token (media, metadata, launch, identity links). */
  auth?: boolean;
  /** Client timeout; /api/tx/send stays open until the transaction confirms (up to ~120 s). */
  timeoutMs?: number;
}

const DEFAULT_TIMEOUT_MS = 30_000;

async function once<T>(path: string, init: ReqInit, token: string | null): Promise<T> {
  const { auth: _auth, timeoutMs = DEFAULT_TIMEOUT_MS, headers, signal, ...rest } = init;
  const t = withTimeout(signal, timeoutMs);
  let res: Response;
  try {
    res = await fetch(API_BASE + path, {
      ...rest,
      signal: t.signal,
      headers: { accept: 'application/json', ...(token ? { authorization: `Bearer ${token}` } : {}), ...(headers as Record<string, string> | undefined) },
    });
  } catch (e) {
    t.done();
    if (signal?.aborted && !t.timedOut()) throw e;
    const why = t.timedOut() ? `no answer within ${Math.round(timeoutMs / 1000)} s` : e instanceof Error ? e.message : String(e);
    throw new ApiError(0, 'network', `Cannot reach the Raydius API (${why}).`);
  }
  let text: string;
  try {
    text = await res.text();
  } catch (e) {
    if (signal?.aborted && !t.timedOut()) throw e;
    throw new ApiError(0, 'network', 'The connection to the Raydius API dropped.');
  } finally {
    t.done();
  }
  let body: unknown;
  try {
    body = text ? JSON.parse(text) : undefined;
  } catch {
    body = undefined;
  }
  if (!res.ok) {
    const err = (body as ApiErrorBody | undefined)?.error;
    const details = err?.details;
    const retryAfterSec = typeof details?.retryAfterSec === 'number' ? details.retryAfterSec : parseRetryAfter(res.headers.get('retry-after'));
    throw new ApiError(res.status, err?.code ?? `http_${res.status}`, err?.message ?? (res.statusText || `HTTP ${res.status}`), details, retryAfterSec);
  }
  return body as T;
}

/**
 * One API request. With `auth`, the Privy access token goes along as a Bearer header; a 401 gets one retry with a
 * fresh token (Privy refreshes an expired one), then NeedsLoginError.
 */
export async function req<T>(path: string, init: ReqInit = {}): Promise<T> {
  if (!init.auth) return once<T>(path, init, null);
  for (let attempt = 0; ; attempt++) {
    const token = await getAccessToken();
    if (!token) {
      if (anonymousAuthAllowed()) return once<T>(path, init, null);
      throw new NeedsLoginError();
    }
    try {
      return await once<T>(path, init, token);
    } catch (e) {
      if (!(e instanceof ApiError) || e.status !== 401) throw e;
      if (attempt >= 1) throw new NeedsLoginError('Your session expired. Log in again.');
    }
  }
}

const qs = (p: Record<string, string | number | undefined | null>) => {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(p)) if (v !== undefined && v !== null && v !== '') u.set(k, String(v));
  const s = u.toString();
  return s ? `?${s}` : '';
};

const json = (body: unknown, extra: ReqInit = {}): ReqInit => ({ method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body), ...extra });

export interface CoinsQuery {
  sort: CoinSort;
  window?: CoinWindow;
  limit?: number;
  cursor?: string | null;
  q?: string;
}

/** POST /api/tx/send stays open until the transaction confirms or its blockhash expires (at most ~120 s). */
export const SEND_TIMEOUT_MS = 135_000;

export const api = {
  health: () => req<HealthResponse>('/api/health'),
  sponsor: () => req<LaunchReadiness['sponsorship']>('/api/sponsor'),
  config: () => req<ConfigResponse>('/api/config'),
  coins: (p: CoinsQuery) => req<CoinsResponse>(`/api/coins${qs({ sort: p.sort, window: p.window, limit: p.limit, cursor: p.cursor, q: p.q })}`),
  coin: (mint: string) => req<CoinDetail>(`/api/coins/${encodeURIComponent(mint)}`),
  trades: (mint: string, p: { limit?: number; before?: string } = {}) => req<TradesResponse>(`/api/coins/${encodeURIComponent(mint)}/trades${qs(p)}`),
  candles: (mint: string, p: { tf: CandleTf; from?: number; to?: number; field?: CandleField }) => req<CandlesResponse>(`/api/coins/${encodeURIComponent(mint)}/candles${qs(p)}`),
  holders: (mint: string, p: { limit?: number } = {}) => req<HoldersResponse>(`/api/coins/${encodeURIComponent(mint)}/holders${qs(p)}`),
  search: (q: string) => req<SearchResponse>(`/api/search${qs({ q })}`),
  profile: (wallet: string) => req<ProfileResponse>(`/api/profile/${encodeURIComponent(wallet)}`),
  leaderboard: (window: LeaderboardWindow) => req<LeaderboardResponse>(`/api/leaderboard${qs({ window })}`),
  uploadMedia: (file: Blob, filename: string) => {
    const fd = new FormData();
    fd.append('file', file, filename);
    // The browser sets the multipart content-type (with its boundary) itself.
    return req<MediaResponse>('/api/media', { method: 'POST', body: fd, auth: true, timeoutMs: 120_000 });
  },
  createMetadata: (body: MetadataRequest) => req<MetadataResponse>('/api/metadata', json(body, { auth: true })),
  airdrop: (body: AirdropRequest) => req<AirdropResponse>('/api/dev/airdrop', json(body)),

  /** Quotes refuse unknown query keys: send exactly these (no cache busters). */
  quote: (q: QuoteQuery, signal?: AbortSignal) =>
    req<QuoteResponse>(
      `/api/quote${qs({ mint: q.mint, side: q.side, amount: q.amount, slippageBps: q.slippageBps, wallet: q.wallet, priorityMicroLamports: q.priorityMicroLamports })}`,
      signal ? { signal } : {},
    ),
  /** The wallet's SOL and its balance of a coin, read at once (limited with quotes). Refuses unknown query keys too. */
  balance: (wallet: string, mint: string, signal?: AbortSignal) => req<BalanceResponse>(`/api/balance${qs({ wallet, mint })}`, signal ? { signal } : {}),
  /** Builds refuse unknown body fields. Omit priorityMicroLamports: on mainnet the server estimates it. */
  txLaunch: (body: LaunchTxRequest) => req<LaunchTxResponse>('/api/tx/launch', json(body, { auth: true })),
  txBuy: (body: BuyTxRequest) => req<TradeTxResponse>('/api/tx/buy', json(body)),
  txSell: (body: SellTxRequest) => req<TradeTxResponse>('/api/tx/sell', json(body)),
  /** Relays signed bytes and waits for confirmation. Use tx/send.ts sendSigned(), which owns the retry rules. */
  txSend: (body: SendTxRequest) => req<SendTxResponse>('/api/tx/send', json(body, { timeoutMs: SEND_TIMEOUT_MS })),

  identity: (wallet: string) => req<WalletIdentity>(`/api/identity/${encodeURIComponent(wallet)}`),
  identities: (wallets: string[]) => req<WalletIdentitiesResponse>(`/api/identity${qs({ wallets: wallets.join(',') })}`),
  linkIdentity: () => req<IdentityLinkResponse>('/api/identity/link', { method: 'POST', auth: true }),
  unlinkIdentity: () => req<IdentityUnlinkResponse>('/api/identity/link', { method: 'DELETE', auth: true }),
};
